'use client'

import Image from 'next/image'
import { MotionConfig, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { TOURNAMENT_GAMES, gameIcon, type TimeFilter } from '@/lib/tournaments'

/**
 * The tournament filter row: game pills plus the Upcoming / Past toggle. The
 * public /tournaments page and the app's tournaments page both render this,
 * so the two stay identical. Pills are one scrollable row on phones and wrap
 * on larger screens.
 */
export function TournamentFilters({
  game,
  onGameChange,
  time,
  onTimeChange,
  pillId,
}: {
  game: string
  onGameChange: (slug: string) => void
  time: TimeFilter
  onTimeChange: (time: TimeFilter) => void
  /** Unique per page: the sliding toggle pill animates within this id. */
  pillId: string
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0 [&::-webkit-scrollbar]:hidden">
        {TOURNAMENT_GAMES.map((g) => (
          <button
            key={g.slug}
            onClick={() => onGameChange(g.slug)}
            aria-pressed={game === g.slug}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors',
              game === g.slug
                ? 'border-primary bg-primary/10'
                : 'border-border bg-card/60 text-muted-foreground hover:text-foreground'
            )}
          >
            <Image src={gameIcon(g.name)} alt="" width={18} height={18} unoptimized className="rounded" />
            {g.name}
          </button>
        ))}
      </div>

      {/* Upcoming / Past toggle */}
      <MotionConfig reducedMotion="user">
        {/* The container is the stacking layer (isolate), not each button: the
            sliding pill then always passes *behind* both labels instead of
            painting over "Upcoming" on its way to "Past". */}
        <div className="isolate flex w-full shrink-0 rounded-full border border-border bg-card/60 p-1 sm:inline-flex sm:w-fit sm:self-auto">
          {(['upcoming', 'past'] as const).map((v) => (
            <button
              key={v}
              onClick={() => onTimeChange(v)}
              aria-pressed={time === v}
              className={cn(
                // Full width on phones (two equal halves), compact from sm up.
                'relative flex-1 rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors sm:flex-none',
                time === v ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {/* Same slide as the app nav pill (AppHeader). */}
              {time === v && (
                <motion.span
                  layoutId={pillId}
                  layoutDependency={time}
                  transition={{ type: 'spring', duration: 0.25, bounce: 0 }}
                  className="absolute inset-0 -z-10 rounded-full bg-secondary"
                />
              )}
              {v}
            </button>
          ))}
        </div>
      </MotionConfig>
    </div>
  )
}
